import { useMemo } from 'react';
import { ConnectedEventInfo } from 'src/components/Events/ConnectedEventInfo';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useContent } from 'src/contexts/ContentContext';
import {
  PosMultiSelect,
  PosMultiSelectProps,
} from 'src/core/POS/PosMultiSelect';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getEventDisplayText,
  getPerformerAndVenueForEvent,
} from 'src/utils/eventWithDataUtils';
import {
  DateTimeRangeWithRelative,
  EventTimeFrameFilter,
  EventWithData,
} from 'src/WebApiController';

type EventSelectorProps = Omit<PosMultiSelectProps, 'valueOptionsContent'> & {
  filterCallback?: (event?: EventWithData) => boolean;
  eventDatesFilter?: DateTimeRangeWithRelative | null;
  eventTimeFrameFilter?: EventTimeFrameFilter | null;
  forceUseCatalogV2?: boolean;
};

export const EventSelector = ({
  filterCallback,
  placeholderText,
  eventDatesFilter,
  eventTimeFrameFilter,
  forceUseCatalogV2,
  ...props
}: EventSelectorProps) => {
  // TODO - remove all these logic once Catalog v2 feature is available
  const { data: catalogData, isLoading } = useCatalogDataContext();
  const unknownStr = useContent(ContentId.Unknown);

  const passesDateFilter = (
    event: EventWithData,
    eventDatesFilter?: DateTimeRangeWithRelative | null
  ) => {
    if (!eventDatesFilter) return true;

    const eventDate = event.event?.dates?.start
      ? new Date(event.event.dates.start)
      : null;
    if (!eventDate) return false;

    const time = eventDate.getTime();
    const now = Date.now();

    // Use relative filters if either is set.
    if (
      eventDatesFilter.relativeStartSecs != null ||
      eventDatesFilter.relativeEndSecs != null
    ) {
      let startBoundary = -Infinity;
      if (eventDatesFilter.relativeStartSecs != null) {
        const relativeStartDate = new Date(
          now + eventDatesFilter.relativeStartSecs * 1000
        );
        // Round to start of day.
        relativeStartDate.setHours(0, 0, 0, 0);
        startBoundary = relativeStartDate.getTime();
      }

      let endBoundary = Infinity;
      if (eventDatesFilter.relativeEndSecs != null) {
        const relativeEndDate = new Date(
          now + eventDatesFilter.relativeEndSecs * 1000
        );
        // Round to end of day.
        relativeEndDate.setHours(23, 59, 59, 999);
        endBoundary = relativeEndDate.getTime();
      }

      return time >= startBoundary && time <= endBoundary;
    } else {
      // Otherwise, use the absolute start/end filters with rounding.
      let startBoundary = -Infinity;
      if (eventDatesFilter.start != null) {
        const startDate = new Date(eventDatesFilter.start);
        startDate.setHours(0, 0, 0, 0); // Round to start of day.
        startBoundary = startDate.getTime();
      }

      let endBoundary = Infinity;
      if (eventDatesFilter.end != null) {
        const endDate = new Date(eventDatesFilter.end);
        endDate.setHours(23, 59, 59, 999); // Round to end of day.
        endBoundary = endDate.getTime();
      }
      return time >= startBoundary && time <= endBoundary;
    }
  };

  const { allEvents, allEventDisplay, allEventSort } = useMemo(() => {
    if (!catalogData?.events) {
      return { allEvents: {}, allEventDisplay: {}, allEventSort: {} };
    }

    const allEvents = {} as Record<string, string>;
    const allEventDisplay = {} as Record<string, () => JSX.Element>;
    const allEventSort = {} as Record<string, string>;

    Object.values(catalogData.events).forEach((event) => {
      if (
        event &&
        event.event &&
        event.event.virtId &&
        (filterCallback == null || filterCallback(event)) &&
        passesDateFilter(event, eventDatesFilter)
      ) {
        const { venue, performer } = getPerformerAndVenueForEvent(
          event.event,
          catalogData
        );

        // Build the display text for the event
        allEvents[event.event.virtId] = getEventDisplayText(
          event.event,
          venue,
          performer,
          true
        );

        // Build the display component for the event
        allEventDisplay[event.event.virtId] = () => (
          <ConnectedEventInfo
            showYear={true}
            showEventDateTbd
            event={event.event}
            performer={performer}
            venue={venue}
            eventNameOnly={false}
            slimMode
            useShortName
            truncateTitle
          />
        );

        // Build the sort value for the event
        allEventSort[event.event.virtId] = event.event?.dates?.start
          ? new Date(event.event.dates.start).toISOString()
          : unknownStr;
      }
    });

    return { allEvents, allEventDisplay, allEventSort };
  }, [catalogData, filterCallback, eventDatesFilter, unknownStr]);

  const hasNoEvent = Object.values(allEvents).length == 0;

  return (
    <PosMultiSelect
      {...props}
      searchable
      loading={isLoading}
      placeholderText={
        hasNoEvent ? ContentId.EventSelectorPlaceholder : placeholderText
      }
      useVirtualWindow
      valueOptionsContent={allEvents}
      valueOptionsDisplay={allEventDisplay}
      valueOptionsSort={allEventSort}
      sortMode="ascending"
      contentProps={{ style: { minWidth: '320px' } }}
    />
  );
};
